import React, { useState, useEffect } from 'react'
import CloseIcon from '../../assets/CloseIcon'
import DotsIcon from '../../assets/DotsIcon'
import EditIcon from '../../assets/EditIcon'
import TrashIcon from '../../assets/TrashIcon'
import SideDrawer from '../Common/SideDrawer/SideDrawer'
import {
    getAllLoansHelper,
    addLoanHelper,
    updateLoanHelper,
    deleteLoanHelper
} from '../../helper/loan-details-helper'

const LOAN_FIELDS = {
    ID: 'id',
    LENDER_NAME: 'lender_name',
    LOAN_AMOUNT: 'loan_amount',
    TENURE_MONTHS: 'tenure_months',
    INTEREST_RATE: 'interest_rate',
    MONTHLY_INTEREST_RATE: 'monthly_interest_rate',
    MONTHLY_INSTALLMENT: 'monthly_installment',
    INSTALLMENT_START_DATE: 'installment_start_date',
    INSTALLMENT_END_DATE: 'installment_end_date',
    OUTSTANDING_BALANCE: 'outstanding_balance'
}

const LoanDetails = ({ onBackClick, onClose }) => {
    const [loanList, setLoanList] = useState([])
    const [open, setOpen] = useState(false)
    const [viewOpen, setViewOpen] = useState(false)
    const [scheduleOpen, setScheduleOpen] = useState(false)
    const [loanData, setLoanData] = useState({})
    const [selectedLoan, setSelectedLoan] = useState(null)
    const [paymentSchedule, setPaymentSchedule] = useState([])
    const [isFieldErrorVisible, setIsFieldErrorVisible] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [activeCardIndex, setActiveCardIndex] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        loadLoans()
    }, [])

    useEffect(() => {
        if (loanData[LOAN_FIELDS.INTEREST_RATE]) {
            const monthlyRate = parseFloat(loanData[LOAN_FIELDS.INTEREST_RATE]) / 12
            setLoanData(prev => ({
                ...prev,
                [LOAN_FIELDS.MONTHLY_INTEREST_RATE]: monthlyRate.toFixed(6)
            }))
        }
    }, [loanData[LOAN_FIELDS.INTEREST_RATE]])

    useEffect(() => {
        const P = parseFloat(loanData[LOAN_FIELDS.LOAN_AMOUNT])
        const n = parseInt(loanData[LOAN_FIELDS.TENURE_MONTHS])
        const yearlyRate = parseFloat(loanData[LOAN_FIELDS.INTEREST_RATE])

        if (P > 0 && n > 0 && yearlyRate > 0) {
            const r = yearlyRate / 12 / 100
            const emi = P * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1)
            setLoanData(prev => ({
                ...prev,
                [LOAN_FIELDS.MONTHLY_INSTALLMENT]: emi.toFixed(2)
            }))
        }
    }, [loanData[LOAN_FIELDS.LOAN_AMOUNT], loanData[LOAN_FIELDS.TENURE_MONTHS], loanData[LOAN_FIELDS.INTEREST_RATE]])

    useEffect(() => {
        calculateInstallmentEndDate()
    }, [loanData[LOAN_FIELDS.INSTALLMENT_START_DATE], loanData[LOAN_FIELDS.TENURE_MONTHS]])

    const calculateInstallmentEndDate = () => {
        if (loanData[LOAN_FIELDS.INSTALLMENT_START_DATE] && loanData[LOAN_FIELDS.TENURE_MONTHS]) {
            const startDate = new Date(loanData[LOAN_FIELDS.INSTALLMENT_START_DATE])
            const tenureMonths = parseInt(loanData[LOAN_FIELDS.TENURE_MONTHS])
            const endDate = new Date(startDate)
            endDate.setMonth(endDate.getMonth() + tenureMonths)
            endDate.setDate(endDate.getDate() - 1)
            
            const formattedEndDate = endDate.toISOString().split('T')[0]
            setLoanData(prev => ({
                ...prev,
                [LOAN_FIELDS.INSTALLMENT_END_DATE]: formattedEndDate
            }))
        }
    }

    const loadLoans = () => {
        getAllLoansHelper()
            .then(res => {
                console.log('Loans loaded: ', res)
                setLoanList(res || [])
            })
            .catch(err => {
                console.log('Error loading Loans: ', err)
                setLoanList([])
            })
    }

    const handleDrawerToggle = () => {
        setOpen(!open)
        if (open) {
            resetForm()
        }
    }

    const handleViewDrawerToggle = () => {
        setViewOpen(!viewOpen)
        if (viewOpen) {
            setSelectedLoan(null)
        }
    }

    const handleScheduleDrawerToggle = () => {
        setScheduleOpen(!scheduleOpen)
        if (scheduleOpen) {
            setSelectedLoan(null)
            setPaymentSchedule([])
        }
    }

    const handleLoanDetailUpdate = (key, value) => {
        setLoanData({
            ...loanData,
            [key]: value
        })
    }

    const resetForm = () => {
        setLoanData({})
        setIsFieldErrorVisible(false)
        setIsEditMode(false)
    }

    const checkValidation = () => {
        return (
            loanData[LOAN_FIELDS.LENDER_NAME] &&
            loanData[LOAN_FIELDS.LOAN_AMOUNT] &&
            loanData[LOAN_FIELDS.TENURE_MONTHS] &&
            loanData[LOAN_FIELDS.INTEREST_RATE] &&
            loanData[LOAN_FIELDS.INSTALLMENT_START_DATE] &&
            loanData[LOAN_FIELDS.OUTSTANDING_BALANCE]
        )
    }

    const handleSubmit = () => {
        if (!checkValidation()) {
            setIsFieldErrorVisible(true)
            return
        }

        setIsFieldErrorVisible(false)
        setIsLoading(true)

        if (isEditMode) {
            updateLoanHelper(loanData)
                .then(res => {
                    console.log('Loan updated: ', res)
                    loadLoans()
                    resetForm()
                    handleDrawerToggle()
                })
                .catch(err => {
                    console.log('Error updating Loan: ', err)
                })
                .finally(() => {
                    setIsLoading(false)
                })
        } else {
            addLoanHelper(loanData)
                .then(res => {
                    console.log('Loan added: ', res)
                    loadLoans()
                    resetForm()
                    handleDrawerToggle()
                })
                .catch(err => {
                    console.log('Error adding Loan: ', err)
                })
                .finally(() => {
                    setIsLoading(false)
                })
        }
    }

    const handleViewLoan = (loan) => {
        setSelectedLoan(loan)
        setActiveCardIndex(null)
        setViewOpen(true)
    }

    const handleEditLoan = (loan) => {
        setLoanData(loan)
        setIsEditMode(true)
        setActiveCardIndex(null)
        setOpen(true)
    }

    const handleDeleteLoan = (loanId) => {
        deleteLoanHelper(loanId)
            .then(res => {
                console.log('Loan deleted: ', res)
                loadLoans()
            })
            .catch(err => {
                console.log('Error deleting Loan: ', err)
            })
        setActiveCardIndex(null)
    }

    const toggleCardMenu = (index, e) => {
        e.stopPropagation()
        setActiveCardIndex(activeCardIndex === index ? null : index)
    }

    const roundHalfUp = (num) => {
        return Number(Math.round(parseFloat(num + 'e2')) + 'e-2')
    }

    const calculatePaymentSchedule = (loan) => {
        const loanAmount = parseFloat(loan[LOAN_FIELDS.LOAN_AMOUNT])
        const tenureMonths = parseInt(loan[LOAN_FIELDS.TENURE_MONTHS])
        const monthlyInterestRate = parseFloat(loan[LOAN_FIELDS.MONTHLY_INTEREST_RATE]) / 100
        const monthlyInstallment = parseFloat(loan[LOAN_FIELDS.MONTHLY_INSTALLMENT])
        const startDate = new Date(loan[LOAN_FIELDS.INSTALLMENT_START_DATE])

        let schedule = []
        let outstandingBalance = loanAmount

        for (let i = 0; i < tenureMonths; i++) {
            const currentDate = new Date(startDate)
            currentDate.setMonth(currentDate.getMonth() + i)
            
            const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
            
            const interestAmount = outstandingBalance * monthlyInterestRate
            const principalAmount = monthlyInstallment - interestAmount
            outstandingBalance = outstandingBalance - principalAmount

            schedule.push({
                date: formatDate(lastDayOfMonth),
                loanAmount: loanAmount.toFixed(2),
                installmentAmount: monthlyInstallment.toFixed(2),
                interestAmount: roundHalfUp(interestAmount).toFixed(2),
                principalAmount: roundHalfUp(principalAmount).toFixed(2),
                outstandingBalance: roundHalfUp(outstandingBalance).toFixed(2)
            })
        }

        return schedule
    }

    const formatDate = (date) => {
        const day = date.getDate().toString().padStart(2, '0')
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        const month = monthNames[date.getMonth()]
        const year = date.getFullYear().toString().slice(-2)
        return `${day}-${month}-${year}`
    }

    const formatDisplayDate = (dateString) => {
        if (!dateString) return '-'
        const date = new Date(dateString)
        return formatDate(date)
    }

    const formatCurrency = (value) => {
        if (!value && value !== 0) return '-'
        return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    const handleShowSchedule = (loan) => {
        setSelectedLoan(loan)
        const schedule = calculatePaymentSchedule(loan)
        setPaymentSchedule(schedule)
        setViewOpen(false)
        setScheduleOpen(true)
    }

    return (
        <>
            <div className="pageBodyRightPanel">
                <div className="pageBodyRightPanelHeader dFlex jcSpaceBetween aiCenter webVisible mb-5">
                    <div className="pageBodyRightPanelHeading">Loan Details</div>
                    <div className="addProductBtn dFlex aiCenter">
                        <button
                            className="btn btnPrimary"
                            onClick={handleDrawerToggle}>
                            ADD
                        </button>
                        {onClose && (
                            <div
                                className="pointer ml-4"
                                onClick={() => onClose()}>
                                <CloseIcon />
                            </div>
                        )}
                    </div>
                </div>

                <div className="pageBodyPanelWrap dFlex settingsDetailsContent">
                    <div className="pageBodyLeftPanel settingsDetailsList">
                {loanList.length === 0 ? (
                    <div className="emptyState">
                        <p>No loan details added yet. Click ADD to add your first loan.</p>
                    </div>
                ) : (
                    <div className="bankListWrap loanListWrap">
                        <table className="bankTable">
                            <thead>
                                <tr>
                                    <th>Lender Name</th>
                                    <th>Loan Amount</th>
                                    <th>Outstanding</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loanList.map((loan, index) => (
                                    <tr
                                        key={loan[LOAN_FIELDS.ID] || index}
                                        className="bankRow"
                                        onClick={() => handleViewLoan(loan)}>
                                        <td className="bankName">
                                            {loan[LOAN_FIELDS.LENDER_NAME]}
                                        </td>
                                        <td>
                                            {formatCurrency(loan[LOAN_FIELDS.LOAN_AMOUNT])}
                                        </td>
                                        <td>
                                            {formatCurrency(loan[LOAN_FIELDS.OUTSTANDING_BALANCE])}
                                        </td>
                                        <td className="bankActions">
                                            <div className="actionWrapper">
                                                <span
                                                    className="dotsIcon pointer"
                                                    onClick={(e) => toggleCardMenu(index, e)}>
                                                    <DotsIcon />
                                                </span>
                                                {activeCardIndex === index && (
                                                    <ul className="cardActionsWrapper show">
                                                        <li
                                                            className="actionItem"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handleEditLoan(loan)
                                                            }}>
                                                            <EditIcon /> Edit
                                                        </li>
                                                        <li
                                                            className="actionItem"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handleDeleteLoan(loan[LOAN_FIELDS.ID])
                                                            }}>
                                                            <TrashIcon /> Delete
                                                        </li>
                                                    </ul>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                    </div>
                </div>

                {/* Add/Edit Loan Drawer */}
                <SideDrawer
                    handleDrawerToggle={handleDrawerToggle}
                    open={open}
                    downloadButton={true}
                    downloadText={'SAVE'}
                    drawerHeading={isEditMode ? 'Edit Loan' : 'Add New Loan'}
                    handleClick={() => handleSubmit()}
                    isLoading={isLoading}>
                    <div className="wizardFieldset">
                        <form className="mt-5">
                            <div className="formGroup formFloating fgrow1">
                                <input
                                    type="text"
                                    className="formControl"
                                    id="lenderName"
                                    placeholder="Lender Name"
                                    value={loanData[LOAN_FIELDS.LENDER_NAME] || ''}
                                    onChange={e =>
                                        handleLoanDetailUpdate(
                                            LOAN_FIELDS.LENDER_NAME,
                                            e.target.value
                                        )
                                    }
                                    required
                                />
                                <label htmlFor="lenderName">Lender Name</label>
                                {isFieldErrorVisible &&
                                    !loanData[LOAN_FIELDS.LENDER_NAME] && (
                                        <div className="invalidFeedback">
                                            Please enter Lender Name.
                                        </div>
                                    )}
                            </div>
                            <div className="formGroup formFloating fgrow1">
                                <input
                                    type="number"
                                    className="formControl"
                                    id="loanAmount"
                                    placeholder="Loan Amount"
                                    value={loanData[LOAN_FIELDS.LOAN_AMOUNT] || ''}
                                    onChange={e =>
                                        handleLoanDetailUpdate(
                                            LOAN_FIELDS.LOAN_AMOUNT,
                                            e.target.value
                                        )
                                    }
                                    min="0"
                                    step="0.01"
                                    required
                                />
                                <label htmlFor="loanAmount">Loan Amount</label>
                                {isFieldErrorVisible &&
                                    !loanData[LOAN_FIELDS.LOAN_AMOUNT] && (
                                        <div className="invalidFeedback">
                                            Please enter Loan Amount.
                                        </div>
                                    )}
                            </div>
                            <div className="formGroup formFloating fgrow1">
                                <input
                                    type="number"
                                    className="formControl"
                                    id="tenureMonths"
                                    placeholder="Tenure (Months)"
                                    value={loanData[LOAN_FIELDS.TENURE_MONTHS] || ''}
                                    onChange={e =>
                                        handleLoanDetailUpdate(
                                            LOAN_FIELDS.TENURE_MONTHS,
                                            e.target.value
                                        )
                                    }
                                    min="1"
                                    required
                                />
                                <label htmlFor="tenureMonths">Tenure (Months)</label>
                                {isFieldErrorVisible &&
                                    !loanData[LOAN_FIELDS.TENURE_MONTHS] && (
                                        <div className="invalidFeedback">
                                            Please enter Tenure.
                                        </div>
                                    )}
                            </div>
                            <div className="formGroup formFloating fgrow1">
                                <input
                                    type="number"
                                    className="formControl"
                                    id="interestRate"
                                    placeholder="Interest Rate (%)"
                                    value={loanData[LOAN_FIELDS.INTEREST_RATE] || ''}
                                    onChange={e =>
                                        handleLoanDetailUpdate(
                                            LOAN_FIELDS.INTEREST_RATE,
                                            e.target.value
                                        )
                                    }
                                    min="0"
                                    step="0.01"
                                    required
                                />
                                <label htmlFor="interestRate">Interest Rate (% per year)</label>
                                {isFieldErrorVisible &&
                                    !loanData[LOAN_FIELDS.INTEREST_RATE] && (
                                        <div className="invalidFeedback">
                                            Please enter Interest Rate.
                                        </div>
                                    )}
                            </div>
                            <div className="formGroup formFloating fgrow1">
                                <input
                                    type="text"
                                    className="formControl"
                                    id="monthlyInterestRate"
                                    placeholder="Monthly Interest (%)"
                                    value={loanData[LOAN_FIELDS.MONTHLY_INTEREST_RATE] || ''}
                                    readOnly
                                    disabled
                                />
                                <label htmlFor="monthlyInterestRate">Monthly Interest (%) - Auto Calculated</label>
                            </div>
                            <div className="formGroup formFloating fgrow1">
                                <input
                                    type="text"
                                    className="formControl"
                                    id="monthlyInstallment"
                                    placeholder="Monthly Installment"
                                    value={loanData[LOAN_FIELDS.MONTHLY_INSTALLMENT] || ''}
                                    readOnly
                                    disabled
                                />
                                <label htmlFor="monthlyInstallment">Monthly Installment - Auto Calculated</label>
                            </div>
                            <div className="formGroup formFloating fgrow1">
                                <input
                                    type="date"
                                    className="formControl"
                                    id="installmentStartDate"
                                    placeholder="Installment Start Date"
                                    value={loanData[LOAN_FIELDS.INSTALLMENT_START_DATE] || ''}
                                    onChange={e =>
                                        handleLoanDetailUpdate(
                                            LOAN_FIELDS.INSTALLMENT_START_DATE,
                                            e.target.value
                                        )
                                    }
                                    required
                                />
                                <label htmlFor="installmentStartDate">Installment Start Date</label>
                                {isFieldErrorVisible &&
                                    !loanData[LOAN_FIELDS.INSTALLMENT_START_DATE] && (
                                        <div className="invalidFeedback">
                                            Please select Installment Start Date.
                                        </div>
                                    )}
                            </div>
                            <div className="formGroup formFloating fgrow1">
                                <input
                                    type="date"
                                    className="formControl"
                                    id="installmentEndDate"
                                    placeholder="Installment End Date"
                                    value={loanData[LOAN_FIELDS.INSTALLMENT_END_DATE] || ''}
                                    readOnly
                                    disabled
                                />
                                <label htmlFor="installmentEndDate">Installment End Date - Auto Calculated</label>
                            </div>
                            <div className="formGroup formFloating fgrow1">
                                <input
                                    type="number"
                                    className="formControl"
                                    id="outstandingBalance"
                                    placeholder="Outstanding Balance"
                                    value={loanData[LOAN_FIELDS.OUTSTANDING_BALANCE] || ''}
                                    onChange={e =>
                                        handleLoanDetailUpdate(
                                            LOAN_FIELDS.OUTSTANDING_BALANCE,
                                            e.target.value
                                        )
                                    }
                                    min="0"
                                    step="0.01"
                                    required
                                />
                                <label htmlFor="outstandingBalance">Outstanding Balance</label>
                                {isFieldErrorVisible &&
                                    !loanData[LOAN_FIELDS.OUTSTANDING_BALANCE] && (
                                        <div className="invalidFeedback">
                                            Please enter Outstanding Balance.
                                        </div>
                                    )}
                            </div>
                        </form>
                    </div>
                </SideDrawer>

                {/* View Loan Drawer */}
                <SideDrawer
                    handleDrawerToggle={handleViewDrawerToggle}
                    open={viewOpen}
                    drawerHeading="Loan Details">
                    {selectedLoan && (
                        <div className="wizardFieldset">
                            <div className="bankViewDetails mt-5">
                                <div className="viewItem">
                                    <div className="viewLabel">Lender Name</div>
                                    <div className="viewValue">
                                        {selectedLoan[LOAN_FIELDS.LENDER_NAME] || '-'}
                                    </div>
                                </div>
                                <div className="viewItem">
                                    <div className="viewLabel">Loan Amount</div>
                                    <div className="viewValue">
                                        {formatCurrency(selectedLoan[LOAN_FIELDS.LOAN_AMOUNT])}
                                    </div>
                                </div>
                                <div className="viewItem">
                                    <div className="viewLabel">Tenure (Months)</div>
                                    <div className="viewValue">
                                        {selectedLoan[LOAN_FIELDS.TENURE_MONTHS] || '-'}
                                    </div>
                                </div>
                                <div className="viewItem">
                                    <div className="viewLabel">Interest Rate (%)</div>
                                    <div className="viewValue">
                                        {selectedLoan[LOAN_FIELDS.INTEREST_RATE] ? `${selectedLoan[LOAN_FIELDS.INTEREST_RATE]}%` : '-'}
                                    </div>
                                </div>
                                <div className="viewItem">
                                    <div className="viewLabel">Monthly Interest (%)</div>
                                    <div className="viewValue">
                                        {selectedLoan[LOAN_FIELDS.MONTHLY_INTEREST_RATE] ? `${parseFloat(selectedLoan[LOAN_FIELDS.MONTHLY_INTEREST_RATE]).toFixed(4)}%` : '-'}
                                    </div>
                                </div>
                                <div className="viewItem">
                                    <div className="viewLabel">Monthly Installment</div>
                                    <div className="viewValue">
                                        {formatCurrency(selectedLoan[LOAN_FIELDS.MONTHLY_INSTALLMENT])}
                                    </div>
                                </div>
                                <div className="viewItem">
                                    <div className="viewLabel">Installment Start Date</div>
                                    <div className="viewValue">
                                        {formatDisplayDate(selectedLoan[LOAN_FIELDS.INSTALLMENT_START_DATE])}
                                    </div>
                                </div>
                                <div className="viewItem">
                                    <div className="viewLabel">Installment End Date</div>
                                    <div className="viewValue">
                                        {formatDisplayDate(selectedLoan[LOAN_FIELDS.INSTALLMENT_END_DATE])}
                                    </div>
                                </div>
                                <div className="viewItem">
                                    <div className="viewLabel">Outstanding Balance</div>
                                    <div className="viewValue outstandingValue">
                                        {formatCurrency(selectedLoan[LOAN_FIELDS.OUTSTANDING_BALANCE])}
                                    </div>
                                </div>
                                <div className="moreDetailsBtn mt-4">
                                    <button
                                        className="btn btnPrimary w-100"
                                        onClick={() => handleShowSchedule(selectedLoan)}>
                                        More Details
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </SideDrawer>

                {/* Payment Schedule Drawer */}
                <SideDrawer
                    handleDrawerToggle={handleScheduleDrawerToggle}
                    open={scheduleOpen}
                    drawerHeading="Payment Schedule"
                    drawerWidth="wide">
                    {selectedLoan && paymentSchedule.length > 0 && (
                        <div className="wizardFieldset">
                            <div className="loanSummary mt-4 mb-4">
                                <div className="summaryRow">
                                    <span className="summaryLabel">Lender:</span>
                                    <span className="summaryValue">{selectedLoan[LOAN_FIELDS.LENDER_NAME]}</span>
                                </div>
                                <div className="summaryRow">
                                    <span className="summaryLabel">Loan Amount:</span>
                                    <span className="summaryValue">{formatCurrency(selectedLoan[LOAN_FIELDS.LOAN_AMOUNT])}</span>
                                </div>
                                <div className="summaryRow">
                                    <span className="summaryLabel">Tenure:</span>
                                    <span className="summaryValue">{selectedLoan[LOAN_FIELDS.TENURE_MONTHS]} months</span>
                                </div>
                                <div className="summaryRow">
                                    <span className="summaryLabel">Interest Rate:</span>
                                    <span className="summaryValue">{selectedLoan[LOAN_FIELDS.INTEREST_RATE]}% p.a.</span>
                                </div>
                            </div>
                            <div className="paymentScheduleTable">
                                <table className="scheduleTable">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Loan Amount</th>
                                            <th>Installment</th>
                                            <th>Interest</th>
                                            <th>Principal</th>
                                            <th>Outstanding</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paymentSchedule.map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.date}</td>
                                                <td>{formatCurrency(row.loanAmount)}</td>
                                                <td>{formatCurrency(row.installmentAmount)}</td>
                                                <td>{formatCurrency(row.interestAmount)}</td>
                                                <td>{formatCurrency(row.principalAmount)}</td>
                                                <td className={parseFloat(row.outstandingBalance) <= 0 ? 'paidOff' : ''}>
                                                    {parseFloat(row.outstandingBalance) < 0
                                                        ? `(${formatCurrency(Math.abs(parseFloat(row.outstandingBalance)))})`
                                                        : formatCurrency(row.outstandingBalance)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </SideDrawer>
            </div>
        </>
    )
}

export default LoanDetails
